import { router } from "@inertiajs/react";
import { Link } from "@inertiajs/react";
import { BackofficeLayout } from "@/components/layout/BackofficeLayout";
import { SearchInput } from "@/components/shared/SearchInput";
import { Pagination, PaginationMeta } from "@/components/shared/Pagination";
import { StageBadge } from '@/components/shared/StageBadge';
import { Button } from "@/components/ui/button";
import { FileText, Plus } from "lucide-react";
import { formatDate } from "@/lib/utils";

type Formation = {
  id: string;
  legal_name: string;
  assumed_name: string | null;
  stage: string;
  entity_name: string;
  jurisdiction_code: string;
  account_name: string;
  account_slug: string;
  paid: boolean | null;
  created_at: string;
};

type Props = {
  formations: Formation[];
  search: string;
  pagination: PaginationMeta;
};

export default function BackofficeFormationsIndex({
  formations,
  search,
  pagination,
}: Props) {
  return (
    <BackofficeLayout
      title="Formations"
      breadcrumbs={[
        { label: "Backoffice", href: "/backoffice/dashboard" },
        { label: "Formations" },
      ]}
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Formations</h1>
          <p className="text-sm text-muted-foreground">
            {pagination.total} total
          </p>
        </div>
        <Button size="sm" asChild>
          <Link href="/backoffice/formations/new">
            <Plus className="w-3.5 h-3.5 mr-1" />
            New Formation
          </Link>
        </Button>
      </div>

      <SearchInput value={search} placeholder="Search formations..." />

      <div className="mt-4">
        {formations.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <FileText className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">
              No formations found
            </h3>
            <p className="text-sm text-muted-foreground">
              Formations will appear here as users create them.
            </p>
          </div>
        ) : (
          <div className="border rounded-lg">
            {formations.map((f, i) => (
              <div
                key={f.id}
                className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? "border-t" : ""}`}
                onClick={() => router.visit(`/backoffice/formations/${f.id}`)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-xs font-medium text-muted-foreground">
                      {f.legal_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">
                        {f.legal_name}
                      </span>
                      <StageBadge stage={f.stage} variant="formation" />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {f.entity_name} · {f.jurisdiction_code} · {f.account_name}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0 ml-4">
                  {formatDate(f.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  );
}
